import { Link } from 'react-router-dom';
import { useReducedMotion } from 'framer-motion';
import { FaArrowRight, FaLock } from 'react-icons/fa';
import { handleHomeClick, playSound } from '../../utils/utils';
import WarpLink from '../../components/WarpTransition/WarpLink';
import GlitchCoreScene from '../Corebound/GlitchCoreScene';
import './SinglePlayerGames.css';

const SinglePlayerGames = () => {
  const reduceMotion = useReducedMotion();

  return (
    <main className="soloMenu">
      <header className="soloMenuTopbar">
        <Link
          className="soloMenuBack neonButton"
          to="/"
          onClick={handleHomeClick}
        >
          ← Home
        </Link>
        <span className="soloNetworkStatus">
          <i aria-hidden="true" /> Portal network unstable
        </span>
      </header>
      <div className="soloMenuIntro">
        <span className="soloEyebrow">
          SLASH / ROLL <span>SOLO CHANNEL</span>
        </span>
        <h1>Choose your anomaly</h1>
        <p>The network is fractured. One breach is still open.</p>
      </div>

      <section className="soloCoreFeature" aria-labelledby="soloCoreTitle">
        <div className="soloCoreCopy">
          <div className="soloCoreEyebrow">
            <span className="soloLiveBadge">
              <i aria-hidden="true" /> BREACH OPEN
            </span>
            <span>01 / COREBOUND</span>
          </div>
          <span className="soloCoreGenre">A SOLO DICE ROGUELIKE</span>
          <h2 id="soloCoreTitle">Corebound</h2>
          <p className="soloCoreTagline">
            Something on the other side
            <br />
            is rolling against you.
          </p>
          <p className="soloCoreDescription">
            Weaponize your dice. Collect powerful relics. Push through a
            corrupted galaxy to confront the Glitch Core.
          </p>
          <WarpLink
            className="soloCoreLaunch"
            to="/single-player/corebound"
            label="BREACH INITIATED / COREBOUND"
            variant="portal"
            aria-label="Enter Corebound"
            onClick={() =>
              playSound('/sounds/intro_to_corebound.mp3', {
                preventOverlap: true,
              })
            }
          >
            <span className="soloLaunchSigil" aria-hidden="true">
              <svg viewBox="0 0 64 64" fill="none">
                <circle
                  className="soloLaunchSigilOrbit"
                  cx="32"
                  cy="32"
                  r="27"
                />
                <circle
                  className="soloLaunchSigilInner"
                  cx="32"
                  cy="32"
                  r="21"
                />
                <path
                  className="soloLaunchSigilCore"
                  d="m32 14 13 18-13 18-13-18Z M19 32h26M32 14v36"
                />
                <path
                  className="soloLaunchSigilTicks"
                  d="M32 0v5M32 59v5M0 32h5M59 32h5"
                />
              </svg>
            </span>
            <span className="soloCoreLaunchCopy">
              <small>OPEN THE RIFT</small>
              <strong>BREACH THE CORE</strong>
            </span>
            <span className="soloCoreLaunchArrow" aria-hidden="true">
              <FaArrowRight />
            </span>
            <span className="soloLaunchEnergy" aria-hidden="true" />
          </WarpLink>
          <div className="soloCoreFootnote">
            <span>FREE TO PLAY</span>
            <span>VERSION 2.1</span>
          </div>
        </div>

        <div className="soloCoreVisual" aria-hidden="true">
          <div className="soloCoreAura" />
          <div className="soloCoreOrb">
            <GlitchCoreScene
              stage="ready"
              className="soloCoreScene"
              decorative
              reduceMotion={!!reduceMotion}
            />
          </div>
          <svg className="soloContainment" viewBox="0 0 480 480" fill="none">
            <circle
              className="soloContainmentOuter"
              cx="240"
              cy="240"
              r="211"
            />
            <circle
              className="soloContainmentInner"
              cx="240"
              cy="240"
              r="185"
            />
            <circle
              className="soloContainmentGuide"
              cx="240"
              cy="240"
              r="227"
            />
            <path
              className="soloContainmentBrackets"
              d="M30 120V70h50 M400 30h50v50 M450 360v50h-50 M80 450H30v-50"
            />
            <path
              className="soloContainmentCrosshair"
              d="M240 0v16 M240 464v16 M0 240h16 M464 240h16"
            />
          </svg>
          <div className="soloCoreParticles">
            {Array.from({ length: 12 }, (_, i) => (
              <i
                key={i}
                style={{
                  '--particle-angle': `${i * 30}deg`,
                  '--particle-delay': `${i * -0.5}s`,
                }}
              />
            ))}
          </div>
          <span className="soloCoreVisualLabel">
            GLITCH CORE <span>{'//'}</span> LIVE SIGNAL
          </span>
          <div className="soloCoreSignal">
            <span className="soloCoreSignalIdle">CONTAINMENT UNSTABLE</span>
            <span className="soloCoreSignalDetected">SIGNAL DETECTED</span>
            <span className="soloSignalBars">
              {Array.from({ length: 7 }, (_, i) => (
                <i key={i} />
              ))}
            </span>
          </div>
        </div>
      </section>

      <div className="soloSealedHeading">
        <span>BEYOND THE BREACH</span>
        <span>MORE ANOMALIES ARE FORMING</span>
      </div>
      <div className="soloSealedGrid">
        <section
          className="soloSealedCard soloSealedCursed"
          role="group"
          aria-disabled="true"
          aria-label="The Cursed Slot Machine. In development and unavailable."
        >
          <div className="soloSealedCopy">
            <span className="soloSealedNumber">02 / CURSED FREQUENCY</span>
            <h2>The Cursed Slot Machine</h2>
            <p>Every digit has a price.</p>
            <span className="soloSealedStatus">
              <FaLock aria-hidden="true" /> SEALED · IN DEVELOPMENT
            </span>
          </div>
          <svg
            className="soloSealedArt soloSlotArt"
            viewBox="0 0 160 150"
            fill="none"
            aria-hidden="true"
          >
            <rect x="8" y="25" width="144" height="102" rx="12" />
            <path d="M15 44h130M15 110h130" />
            {[22, 65, 108].map((x, i) => (
              <g key={x}>
                <rect x={x} y="52" width="31" height="50" rx="5" />
                <text x={x + 15.5} y="87" textAnchor="middle">
                  {i === 1 ? '?' : '7'}
                </text>
              </g>
            ))}
            <path
              className="soloGlassCrack"
              d="M98 5 83 35 96 53 72 78 80 96 59 143M96 53 131 45 155 61M72 78 41 68 19 83M83 35 57 25"
            />
            <path className="soloSealedArtSeal" d="M12 116 145 37" />
          </svg>
        </section>
        <section
          className="soloSealedCard soloSealedDungeon"
          role="group"
          aria-disabled="true"
          aria-label="Dice Dungeon. In development and unavailable."
        >
          <div className="soloSealedCopy">
            <span className="soloSealedNumber">03 / DEPTHS UNKNOWN</span>
            <h2>Dice Dungeon</h2>
            <p>Some doors stay closed for a reason.</p>
            <span className="soloSealedStatus">
              <FaLock aria-hidden="true" /> SEALED · IN DEVELOPMENT
            </span>
          </div>
          <svg
            className="soloSealedArt soloDungeonArt"
            viewBox="0 0 160 150"
            fill="none"
            aria-hidden="true"
          >
            <path
              className="soloDungeonDoor"
              d="M29 137V65a51 51 0 0 1 102 0v72Z"
            />
            <path d="M41 137V66a39 39 0 0 1 78 0v71 M28 69h14 M31 45l13 6 M46 25l9 11 M71 15l3 13 M98 18l-5 12 M121 35l-12 8 M131 59l-13 3" />
            <path
              className="soloDungeonDepth"
              d="m56 137 12-35h24l12 35 M63 117h34 M59 128h42 M69 103V72a11 11 0 0 1 22 0v31"
            />
            <path
              className="soloSealedArtSeal"
              d="m18 58 123 61 M18 119l123-61"
            />
            <rect
              className="soloDungeonLock"
              x="67"
              y="78"
              width="26"
              height="23"
              rx="4"
            />
            <path d="M73 78v-7a7 7 0 0 1 14 0v7 M80 87v5" />
          </svg>
        </section>
      </div>

      <footer className="soloMenuFooter">
        <span>SLASH / ROLL</span> FORTUNE FAVORS THE UNSTABLE.
      </footer>
    </main>
  );
};

export default SinglePlayerGames;
